import "./buffer-polyfill";
import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "@fontsource/poppins/300.css";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import "@fontsource-variable/inter";
import "@fontsource/caveat/600.css";
import * as Sentry from "@sentry/react";
import { Spin } from "antd";
import "react-indiana-drag-scroll/dist/style.css";
import App from "./App";
import "./i18n";
import { LoadingOutlined } from "@ant-design/icons";
import ErrorBoundary from "./components/ErrorBoundary";
// Import pdfjsConfig to trigger module-level worker configuration
import "./utils/pdfjsConfig";
// Initialize OAuth before React mounts - required for Amplify v6 OAuth callback processing
// safer here than in AuthManager to avoid race conditions
import { captureOAuthError, initOAuth } from "./services/oauth";
import { withAnalytics } from "@libertify.frontend/analytics";
import {
  scrubSentryBreadcrumb,
  scrubSentryEvent,
} from "@libertify.frontend/helpers";
import { getCookieConsent } from "@libertify/ui";
import { isDocustream, isLocal } from "./helpers/core";
import { syncAnalyticsWithConsent } from "./services/analyticsConsent";

// Capture OAuth errors before Amplify clears URL params
captureOAuthError();
initOAuth();

if (
  import.meta.env.VITE_ENV === "production" ||
  import.meta.env.VITE_ENV === "staging"
) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", import.meta.env.VITE_API_URL],
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
  });
}

const container = document.getElementById("root");
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!container) throw new Error("Root element not found");
const root = createRoot(container);

async function enableMocking() {
  // Only enable MSW when running locally (not in production/staging)
  const env = import.meta.env.VITE_ENV;
  const enableMsw = import.meta.env.VITE_ENABLE_MSW === "true";

  if (env !== "production" && env !== "staging" && enableMsw) {
    const { worker } = await import("./mocks/browser");
    return worker.start({
      onUnhandledRequest: "bypass",
    });
  }
}

try {
  await enableMocking();
} catch (error) {
  console.error("Error enabling mocking", error);
}

const AppWithSegmentAnalytics = withAnalytics(App, {
  // Write key selection: Libertify-specific key takes precedence if available
  segmentWriteKey:
    !isDocustream() && import.meta.env.VITE_SEGMENT_WRITE_KEY_LIBERTIFY
      ? import.meta.env.VITE_SEGMENT_WRITE_KEY_LIBERTIFY
      : import.meta.env.VITE_SEGMENT_WRITE_KEY,
  environment: import.meta.env.VITE_ENV ?? "development",
  appName: "web_media_app",
  collectorHost: import.meta.env.VITE_ANALYTICS_COLLECTOR_HOST,
  onLoadError: (error) =>
    Sentry.captureException(error, { tags: { feature: "analytics" } }),
  debug: isLocal() && import.meta.env.VITE_ENV === "development",
  // don't send events when running locally debugging sessions with staging/prod envs
  // it could send events to prod with shared user data from other environments
  // and create duplicates in hubspot for instance
  // Opt-in: nothing loads or identifies until the cookie banner is accepted
  enabled: getCookieConsent() === "true",
});
// Subscribed before React renders, so Segment is on before AuthInitializer identifies
syncAnalyticsWithConsent();
root.render(
  <React.Suspense fallback={<Spin indicator={<LoadingOutlined />} />}>
    <ErrorBoundary id="root">
      <AppWithSegmentAnalytics />
    </ErrorBoundary>
  </React.Suspense>
);
